import { z } from 'zod';

/**
 * Coerces query-string or boolean `readonly` / flag values to a boolean.
 * Empty string and `null` are treated as missing (`undefined`).
 * `null` is accepted because Nest `@QueryBoolean` returns `null` when the
 * query param is omitted.
 *
 * @param v Raw query value.
 * @returns True when the value is boolean true or the string `"true"`.
 */
const queryBooleanOptional = z
  .union([
    z.literal('true'),
    z.literal('false'),
    z.boolean(),
    z.literal(''),
    z.null(),
  ])
  .optional()
  .transform(v => {
    if (v === undefined || v === '' || v === null) {
      return undefined;
    }
    return v === true || v === 'true';
  });

/** Optional field mask for GET /users/:id — omit sections to skip joins/hydration. */
export const GetUserByIdFieldsSchema = z
  .array(
    z.enum([
      'status',
      'summary',
      'segments',
      'contacts',
      'hospitalizations',
      'standardAttributes',
      'relationship',
    ])
  )
  .optional();

/**
 * Zod schema for GET /users/:id query params (`readonly`, `include-details`, `fields`).
 */
export const GetUserByIdQuerySchema = z.object({
  readonly: queryBooleanOptional,
  'include-details': queryBooleanOptional,
  fields: z
    .string()
    .optional()
    .transform(s =>
      s
        ? s
            .split(',')
            .map(f => f.trim())
            .filter(Boolean)
        : undefined
    )
    .pipe(GetUserByIdFieldsSchema.optional()),
});

/** Parsed GET /users/:id query contract. */
export type GetUserByIdQueryDto = z.infer<typeof GetUserByIdQuerySchema>;
